import React, { useState, useEffect } from 'react';
import { PlayCircle, X, Menu, TrendingUp, Smartphone, Check, Target, ShieldCheck } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const brandLogo = `${process.env.PUBLIC_URL}/NuvioLogo.png?v=20260314c`;
  const navLinks = [
    {
      name: "About",
      href: "/home/about",
      children: [{ name: "Why Nuvio?", href: "/home/why-nuvio" }],
    },
    { name: "For Coaches", href: "/home/programs" },
    { name: "For Athletes", href: "/home/athletes" },
    { name: "Features", href: "/home/features" },
    { name: "Pricing", href: "/home/pricing" },
    { name: "Case Studies", href: "/home/case-studies" },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled || isOpen ? 'bg-white/95 backdrop-blur-md border-b border-slate-100 shadow-sm' : 'bg-white border-b border-transparent'
        }`}
    >
      <div className="max-w-7xl mx-auto w-full px-6 h-20 flex justify-between items-center">
        {/* Logo */}
        <a href="/home" className="flex items-center gap-2 cursor-pointer group">
          <img
            src={brandLogo}
            alt="Nuvio"
            className="w-8 h-8 rounded-lg object-cover shadow-sm transition-transform group-hover:scale-105"
          />
          <span className="text-xl font-bold tracking-tight text-slate-900">
            Nuvio<span className="text-blue-600">Sport</span>
          </span>
        </a>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) =>
            link.children ? (
              <div key={link.name} className="relative group">
                <a
                  href={link.href}
                  className="text-sm font-semibold text-slate-600 hover:text-blue-600 transition-colors"
                >
                  {link.name}
                </a>
                <div className="absolute left-0 top-full pt-3 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all">
                  <div className="bg-white border border-slate-100 shadow-xl rounded-xl p-3 min-w-[180px]">
                    {link.children.map((child) => (
                      <a
                        key={child.name}
                        href={child.href}
                        className="block px-3 py-2 text-sm font-semibold text-slate-700 rounded-lg hover:bg-slate-50 hover:text-blue-600"
                      >
                        {child.name}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <a
                key={link.name}
                href={link.href}
                className={`text-sm font-semibold transition-colors ${
                  link.name === "For Athletes"
                    ? "text-blue-600"
                    : "text-slate-600 hover:text-blue-600"
                }`}
              >
                {link.name}
              </a>
            )
          )}
          <a href="/login" className="text-sm font-bold text-slate-600 hover:text-blue-600 transition-colors">
            Login
          </a>
          <a href="/create-account" className="px-5 py-2 bg-slate-900 text-white text-sm font-bold rounded-lg hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20">
            Claim Your Spot
          </a>
        </div>

        {/* Mobile Toggle */}
        <button className="md:hidden p-2 text-slate-600" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div className="absolute top-20 left-0 w-full bg-white border-b border-slate-100 p-6 flex flex-col gap-4 md:hidden shadow-xl animate-in slide-in-from-top-5">
          {navLinks.map((link) => (
            <div key={link.name} className="flex flex-col gap-2">
              <a
                href={link.href}
                onClick={() => setIsOpen(false)}
                className={`text-lg font-semibold ${
                  link.name === "For Athletes" ? "text-blue-600" : "text-slate-900"
                }`}
              >
                {link.name}
              </a>
              {link.children && (
                <div className="ml-4 flex flex-col gap-2">
                  {link.children.map((child) => (
                    <a
                      key={child.name}
                      href={child.href}
                      onClick={() => setIsOpen(false)}
                      className="text-sm font-semibold text-slate-600"
                    >
                      {child.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
          <a href="/create-account" className="w-full text-center py-4 bg-slate-900 text-white font-bold rounded-xl">
            Get Started
          </a>
        </div>
      )}
    </nav>
  );
};

const Hero = () => (
  <section className="bg-white pt-32 md:pt-44 pb-24 px-6">
    <div className="max-w-4xl mx-auto text-center">
      <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-[11px] font-bold uppercase tracking-widest mb-8">
        <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse"></span>
        Eliminate the Guesswork
      </div>

      <h1 className="text-5xl md:text-7xl font-extrabold text-slate-900 tracking-tight mb-8 leading-[1.1]">
        Stop Guessing. <br />
        <span className="text-blue-600">Start Executing.</span>
      </h1>

      <p className="text-xl text-slate-500 max-w-2xl mx-auto mb-12 leading-relaxed font-medium">
        Stop following random workouts. Nuvio delivers the exact <strong>performance standards</strong> your coach requires to get you on the court.
      </p>

      <div className="flex flex-col sm:flex-row justify-center gap-4">
        <a href="/create-account" className="px-10 py-5 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all shadow-xl shadow-blue-600/25 text-lg">
          Get Your Development Standard
        </a>
      </div>
    </div>
  </section>
);

const FeatureGrid = () => (
  <section id="the-path" className="py-24 px-6 bg-slate-50 border-y border-slate-100 scroll-mt-24">
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-16">
        <h2 className="text-3xl font-extrabold text-slate-900 mb-4">The Competitive Advantage</h2>
        <p className="text-slate-500 text-lg">Don't just work harder. Work on the right things.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        <div className="p-8 rounded-3xl bg-white border border-slate-200 hover:shadow-xl transition-shadow group">
          <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-6 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-all">
            <Target size={24} />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-3">Coach-Aligned Protocols</h3>
          <p className="text-slate-500 leading-relaxed">No more wondering if you're doing the right drills. You are training exactly the way your coach wants to see you play.</p>
        </div>

        <div className="p-8 rounded-3xl bg-white border border-slate-200 hover:shadow-xl transition-shadow group">
          <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-6 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-all">
            <PlayCircle size={24} />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-3">The Technical Blueprint</h3>
          <p className="text-slate-500 leading-relaxed">Don't waste reps on "empty" volume. Every standard comes with precise technical cues and execution notes, ensuring every movement translates directly to game-day performance.</p>
        </div>

        <div className="p-8 rounded-3xl bg-white border border-slate-200 hover:shadow-xl transition-shadow group">
          <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-6 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-all">
            <TrendingUp size={24} />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-3">Proof of Progress</h3>
          <p className="text-slate-400 leading-relaxed">Coaches can't ignore the data. Your work is logged, verified, and visible, showing them exactly who is ready to lead.</p>
        </div>
      </div>
    </div>
  </section>
);

const Outcomes = () => (
  <section className="py-24 px-6 bg-slate-900 text-white">
    <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-16">
      <div className="flex-1">
        <div className="inline-block p-2 bg-blue-600/20 rounded-lg text-blue-400 mb-6">
          <ShieldCheck size={32} />
        </div>
        <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">Certainty in your Season.</h2>
        <p className="text-slate-400 text-xl mb-10 leading-relaxed">
          When you follow a random workout, you hope you get better. When you execute a <strong>Nuvio Workout</strong>, you know you are building the specific identity your team requires.
        </p>
        <ul className="space-y-6">
          {[
            { title: "Direct System Alignment", desc: "Your skills will fit your team's schemes perfectly." },
            { title: "Eliminated Ambiguity", desc: "Know exactly what it takes to earn playing time." },
            { title: "Verified Discipline", desc: "Build a reputation for elite work ethic with tracked data." }
          ].map((item) => (
            <li key={item.title} className="flex items-start gap-4">
              <div className="mt-1 bg-blue-600 rounded-full p-1"><Check size={16} strokeWidth={3} /></div>
              <div>
                <div className="font-bold text-lg text-white">{item.title}</div>
                <div className="text-slate-400">{item.desc}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex-1 relative w-full">
        {/* Abstract Phone UI Representation */}
        <div className="relative z-10 bg-slate-800 p-8 rounded-[2.5rem] border border-slate-700 shadow-2xl max-w-sm mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Roster Readiness</div>
              <div className="text-2xl font-black text-white">Your Name</div>
            </div>
            <div className="text-green-400 font-black text-2xl">78%</div>
          </div>

          <div className="h-3 bg-slate-700 rounded-full mb-10 overflow-hidden">
            <div className="h-full w-[94%] bg-blue-500 rounded-full shadow-[0_0_15px_rgba(59,130,246,0.5)]"></div>
          </div>

          <div className="space-y-4">
            <div className="p-5 bg-slate-700/40 rounded-2xl flex items-center justify-between border border-slate-700/50">
              <span className="font-bold">Finishing Standards</span>
              <span className="text-[10px] font-black bg-green-500/20 text-green-400 px-3 py-1 rounded-full uppercase tracking-tighter">92%</span>
            </div>
            <div className="p-5 bg-slate-700/40 rounded-2xl flex items-center justify-between border border-slate-700/50">
              <span className="font-bold">Defensive Rotations</span>
              <span className="text-[10px] font-black bg-green-500/20 text-green-400 px-3 py-1 rounded-full uppercase tracking-tighter">88%</span>
            </div>
            <div className="p-5 bg-slate-700/40 rounded-2xl flex items-center justify-between border border-slate-700/50">
              <span className="font-bold">Three Point Shooting</span>
              <span className="text-[10px] font-black bg-green-500/20 text-red-400 px-3 py-1 rounded-full uppercase tracking-tighter">44%</span>
            </div>
          </div>
        </div>
        <div className="absolute -inset-10 bg-blue-600/10 blur-[80px] -z-10 rounded-full"></div>
      </div>
    </div>
  </section>
);

const FeaturePreview = () => (
  <section className="py-20 px-6 bg-white">
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-2">Features</p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">Everything you need to execute</h2>
          <p className="text-slate-600 mt-3 max-w-2xl">
            From skill workouts to progress tracking, your development lives in one place.
          </p>
        </div>
        <a
          href="/home/features"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-slate-900 text-white font-bold hover:bg-slate-800 transition-all"
        >
          View All Features
        </a>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-2">Skill Workouts</h3>
          <p className="text-sm text-slate-600">
            Train with purpose using drills tied directly to your coach&apos;s priorities.
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-2">S&amp;C Programs</h3>
          <p className="text-sm text-slate-600">
            Build strength and conditioning with structured plans and checkpoints.
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-2">Progress Tracking</h3>
          <p className="text-sm text-slate-600">
            See how your work stacks up and focus on what moves you forward.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="py-16 text-center bg-white border-t border-slate-100">
    <div className="text-slate-900 font-bold mb-4">NuvioSport</div>
    <div className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em]">
      &copy; 2026 Nuvio Sport Inc. • The Standard of Excellence
    </div>
  </footer>
);

export default function AthleteLanding() {
  return (
    <div className="w-full min-h-screen bg-white font-sans text-slate-900 overflow-x-hidden selection:bg-blue-600 selection:text-white">
      <Navbar />
      <Hero />
      <FeatureGrid />
      <Outcomes />
      <FeaturePreview />
      <Footer />
    </div>
  );
}
